'use client';

import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import type { GuardrailPipeline } from '@terra/shared-contracts-applications';
import { Dialog } from '../../components/dialog';
import { toasterApi } from '../toast/ToastProvider';
import { GuardrailFormFields } from './GuardrailFormFields';
import {
  hasGuardrailFormErrors,
  toGuardrailFormValues,
  toGuardrailServerErrors,
  validateGuardrailForm,
  type GuardrailFormErrors,
  type GuardrailFormMessages,
  type GuardrailFormSource,
  type GuardrailFormValues,
  type GuardrailSubmitOutcome,
} from './guardrail-form.model';

export type GuardrailFormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guardrail: GuardrailFormSource | null;
  availablePipelines: readonly GuardrailPipeline[];
  onSubmit: (values: GuardrailFormValues) => Promise<GuardrailSubmitOutcome>;
};

export const GuardrailFormDialog = ({
  open,
  onOpenChange,
  guardrail,
  availablePipelines,
  onSubmit,
}: GuardrailFormDialogProps) => {
  const t = useTranslations('designSystem.guardrails');
  const tCommon = useTranslations('designSystem.common');
  const [values, setValues] = useState<GuardrailFormValues>(() =>
    toGuardrailFormValues(guardrail),
  );
  const [errors, setErrors] = useState<GuardrailFormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const isEditing = guardrail !== null;
  const messages: GuardrailFormMessages = useMemo(
    () => ({
      nameRequired: t('validationError.nameRequired'),
      descriptionRequired: t('validationError.descriptionRequired'),
      ruleRequired: t('validationError.ruleContentRequired'),
      maliciousDescription: t('maliciousDescription'),
      maliciousRule: t('maliciousRule'),
    }),
    [t],
  );

  useEffect(() => {
    if (!open) return;
    setValues(toGuardrailFormValues(guardrail));
    setErrors({});
  }, [open, guardrail]);

  const handleSave = async () => {
    if (isSaving) return;
    const formErrors = validateGuardrailForm({ values, messages });

    setErrors(formErrors);
    if (hasGuardrailFormErrors(formErrors)) return;

    setIsSaving(true);

    try {
      const outcome = await onSubmit(values);

      if (outcome.kind === 'invalid') {
        setErrors(
          toGuardrailServerErrors({
            validationErrors: outcome.validationErrors,
            messages,
          }),
        );

        return;
      }

      if (outcome.kind === 'saved') {
        toasterApi.success({
          title: isEditing ? t('success.updated') : t('success.created'),
          description: isEditing
            ? t('success.updatedDescription')
            : t('success.createdDescription'),
        });
      }

      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save guardrail', {
        error: error instanceof Error ? error : new Error(String(error)),
      });
      toasterApi.error({
        title: t('errors.failedToSave'),
        description: t('errors.failedToSave'),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const primaryLabel = isSaving
    ? t(isEditing ? 'loadingStates.saving' : 'loadingStates.adding')
    : isEditing
      ? tCommon('save')
      : t('addGuardrail');

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={isEditing ? t('editGuardrail') : t('addNewGuardrail')}
      description={t('configureDescription')}
      closeLabel={tCommon('close')}
      primaryAction={{
        label: primaryLabel,
        onClick: () => {
          void handleSave();
        },
        loading: isSaving,
        disabled: isSaving,
      }}
      secondaryAction={{
        label: tCommon('cancel'),
        onClick: () => onOpenChange(false),
      }}
    >
      <GuardrailFormFields
        values={values}
        errors={errors}
        availablePipelines={availablePipelines}
        setValues={setValues}
        setErrors={setErrors}
      />
    </Dialog>
  );
};
